import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import axios from 'axios';

export function ResetPassword(): JSX.Element {
  const { token } = useParams({ from: '/reset-password/$token' });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setPending(true);
    try {
      await axios.post('/api/auth/reset-password', { token, password });
      setDone(true);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 400) {
        setError('This reset link is invalid or has expired.');
      } else if (axios.isAxiosError(err) && err.response?.status === 429) {
        setError('Too many attempts. Please wait a few minutes and try again.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setPending(false);
    }
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-sm card-paper p-6 sm:p-8 space-y-4">
          <h1 className="font-sans text-3xl">Password updated</h1>
          <p className="text-sm text-muted-foreground">Your password has been changed. You can sign in with it now.</p>
          <Link to="/login" className="btn-primary inline-flex">
            Go to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm card-paper p-6 sm:p-8 space-y-6">
        <h1 className="font-sans text-3xl">Choose a new password</h1>
        <form className="space-y-4" onSubmit={submit}>
          <div className="space-y-1.5">
            <label htmlFor="rp-password" className="field-label">New password</label>
            <input
              id="rp-password"
              className="input"
              type="password"
              placeholder="New password (≥ 8 chars)"
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="rp-confirm" className="field-label">Confirm password</label>
            <input
              id="rp-confirm"
              className="input"
              type="password"
              placeholder="Confirm new password"
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          </div>
          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}{' '}
              {error.startsWith('This reset link') && (
                <Link to="/forgot-password" className="underline underline-offset-4">Request a new one</Link>
              )}
            </p>
          )}
          <button type="submit" disabled={pending} className="btn-primary w-full">
            {pending ? 'Saving…' : 'Set new password'}
          </button>
        </form>
      </div>
    </div>
  );
}
